/**
 * The one ritual that ends onboarding. No screen may copy it.
 *
 * Order matters: `completeOnboarding` first, because the route guard in
 * `app/_layout.tsx` auto-completes onboarding the moment a week bucket is
 * non-empty — writing the week first would fire that path with no payload and
 * the funnel would lose the mode and the duration.
 */
import { completeOnboarding, type OnboardingDirection, type OnboardingState } from '../redux/onboardingSlice';
import { continueAsGuest } from '../redux/authSlice';
import { setWeeklyPlan, setCurrentWeek } from '../redux/mealPlanSlice';
import { buildGroceryList } from '../redux/groceryListSlice';
import { groupMealsByWeek } from './horizonWeek';

export type FinishRoute = '/(app)' | '/(app)/recipes';

interface FinishState {
  onboarding: OnboardingState;
  auth: { isAuthenticated: boolean; isGuest: boolean };
}

type Dispatch = (action: unknown) => unknown;

export function finishOnboarding(
  dispatch: Dispatch,
  getState: () => FinishState,
  mode: OnboardingDirection,
  now: Date = new Date(),
): { route: FinishRoute; dinners: number } {
  const state = getState();
  const { generatedWeek, onboardingWeekStart, startedAt } = state.onboarding;
  const dinners = mode === 'plan' ? generatedWeek.length : 0;
  const durationMs = startedAt ? Math.max(0, now.getTime() - new Date(startedAt).getTime()) : 0;

  dispatch(completeOnboarding({ mode, durationMs, dinners }));
  if (!state.auth.isAuthenticated && !state.auth.isGuest) dispatch(continueAsGuest());

  if (mode === 'plan' && onboardingWeekStart && generatedWeek.length > 0) {
    // The horizon is seven days from today, so a plan built mid-week spills
    // past Sunday. One dispatch per bucket — the sync middleware syncs the
    // single week named in the payload, so a spilled plan written in one go
    // would never reach Firestore.
    for (const [weekStartDate, meals] of groupMealsByWeek(generatedWeek)) {
      dispatch(setWeeklyPlan({ weekStartDate, meals, source: 'onboarding' }));
    }
    dispatch(setCurrentWeek(onboardingWeekStart));
    // Pinned to the week the cook lands on, and stretched to the last dinner.
    // Onboarding promises « sept soupers et la liste d'épicerie »; a Thursday
    // signup's list used to cover Thursday to Sunday — four of the seven (#722).
    const lastDinner = generatedWeek.reduce(
      (latest, m) => (m.scheduledFor > latest ? m.scheduledFor : latest),
      generatedWeek[0].scheduledFor,
    );
    dispatch(buildGroceryList({ weekStart: onboardingWeekStart, coversUntil: lastDinner }));
  }

  return { route: mode === 'plan' ? '/(app)' : '/(app)/recipes', dinners };
}
